import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'react-toastify';
import { Plus, ArrowLeft, Save, ChevronDown, Users as UsersIcon, Search } from 'react-feather';
import config from '../../../../config';
import '../../sadmin.css';

const ManageSadminUsers = () => {
    const { t } = useTranslation();
    const token = localStorage.getItem('sessiontoken');
    const configheader = { headers: { Authorization: `${token}` } };
    const login_id = localStorage.getItem('loginId');

    const [users, setUsers] = useState([]);
    const [userId, setUserId] = useState('');
    const [userProfileId, setUserProfileId] = useState('');
    const [viewType, setViewType] = useState(''); // '' | 'Add' | 'Edit'
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [email, setEmail] = useState('');
    const [deletePermission, setDeletePermission] = useState(false);
    const [isDisable, setIsDisable] = useState(false);
    const [concessionaires, setConcessionaires] = useState([]);
    const [selectedConcessionaires, setSelectedConcessionaires] = useState([]);
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [query, setQuery] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const dropdownRef = useRef(null);

    useEffect(() => {
        axios
            .post(`${config.hostname}/users/getusertypeid`, { user_type: 'Backend User' }, configheader)
            .then((res) => setUserProfileId(res.data.userTypeId))
            .catch((err) => console.error(err));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!userProfileId) return;
        axios
            .post(
                `${config.hostname}/users/getusersbytype`,
                { user_type_id: userProfileId },
                configheader,
            )
            .then((res) => setUsers(res.data || []))
            .catch((err) => console.error(err));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userProfileId]);

    useEffect(() => {
        axios
            .get(`${config.hostname}/concessionaire`, configheader)
            .then((res) => setConcessionaires(res.data || []))
            .catch((err) => console.error(err));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Close dropdown on outside click
    useEffect(() => {
        const handler = (e) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
                setDropdownOpen(false);
            }
        };
        if (dropdownOpen) document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [dropdownOpen]);

    const toggleConcessionaire = (id) => {
        setSelectedConcessionaires((prev) =>
            prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
        );
    };

    const clearAll = () => {
        setViewType('');
        setFirstName('');
        setLastName('');
        setEmail('');
        setSelectedConcessionaires([]);
        setDeletePermission(false);
        setIsDisable(false);
        setUserId('');
    };

    const handleUserClick = (id) => {
        setViewType('Edit');
        setUserId(id);
        axios
            .get(`${config.hostname}/users/${id}`, configheader)
            .then((res) => {
                setFirstName(res.data.userFirstName || '');
                setLastName(res.data.userLastName || '');
                setEmail(res.data.username || '');
                setUserProfileId(res.data.user_type_id);
                setIsDisable(!!res.data.user_disable);
                setDeletePermission(!!res.data.data_delete_permission);
                setSelectedConcessionaires(res.data.concessionaire_access || []);
            })
            .catch((err) => console.error(err));
    };

    const handleAddSubmit = async (e) => {
        e.preventDefault();
        if (selectedConcessionaires.length === 0) {
            toast.warning(t('Select any concessionaire'));
            return;
        }
        setSubmitting(true);
        const data = {
            userFirstName: firstName,
            userLastName: lastName,
            username: email,
            password: 'egoal@1',
            user_type_id: userProfileId,
            concessionaire_access: selectedConcessionaires,
            login_id,
            data_delete_permission: deletePermission,
        };
        try {
            const res = await axios.post(`${config.hostname}/users`, data, configheader);
            toast.success(t('Successfully added'));
            setUsers((prev) => [...prev, { ...res.data }]);
            clearAll();
        } catch (err) {
            console.error(err);
            toast.error(t('Error'));
        } finally {
            setSubmitting(false);
        }
    };

    const handleEditSubmit = async (e) => {
        e.preventDefault();
        if (selectedConcessionaires.length === 0) {
            toast.warning(t('Select any concessionaire'));
            return;
        }
        setSubmitting(true);
        const data = {
            userFirstName: firstName,
            userLastName: lastName,
            username: email,
            user_type_id: userProfileId,
            concessionaire_access: selectedConcessionaires,
            login_id,
            data_delete_permission: deletePermission,
            user_disable: isDisable,
        };
        try {
            await axios.post(`${config.hostname}/users/updateuser/${userId}`, data, configheader);
            toast.success(t('Successfully updated'));
            clearAll();
        } catch (err) {
            console.error(err);
            toast.error(t('Error'));
        } finally {
            setSubmitting(false);
        }
    };

    const filtered = (() => {
        const q = query.trim().toLowerCase();
        if (!q) return users;
        return users.filter((u) =>
            [u.userFirstName, u.userLastName, u.username]
                .filter(Boolean)
                .some((v) => String(v).toLowerCase().includes(q)),
        );
    })();

    const selectedSummary =
        selectedConcessionaires.length > 0
            ? concessionaires
                  .filter((c) => selectedConcessionaires.includes(c._id))
                  .map((c) => c.concessionaire_name)
                  .join(', ')
            : t('Select concessionaires');

    // ── List view ────────────────────────────────────────────────────────
    if (viewType === '') {
        return (
            <div className="v3 v3-sadmin">
                <header className="v3-sadmin-hero">
                    <div className="v3-sadmin-hero-text">
                        <div className="v3-label">{t('Settings · Users')}</div>
                        <h1 className="v3-display v3-sadmin-greeting" style={{ fontSize: 26 }}>
                            {t('Backend users')}
                        </h1>
                        <p className="v3-body v3-sadmin-sub">
                            {t('Provision and manage users who can access the backend tooling. Click a row to edit.')}
                        </p>
                    </div>
                    <div className="v3-sadmin-hero-actions">
                        <button
                            type="button"
                            className="v3-btn v3-btn-primary"
                            onClick={() => setViewType('Add')}
                        >
                            <Plus size={14} />
                            {t('New user')}
                        </button>
                    </div>
                </header>

                <section className="v3-card v3-sadmin-list-card">
                    <div className="v3-sadmin-list-header">
                        <div className="v3-sadmin-list-title">
                            <h2 className="v3-heading">{t('Users')}</h2>
                            <span className="v3-sadmin-list-count">{filtered.length}</span>
                        </div>
                        <div className="v3-sadmin-list-actions">
                            <div className="v3-sadmin-search">
                                <Search size={14} aria-hidden />
                                <input
                                    type="search"
                                    placeholder={t('Search name or email')}
                                    value={query}
                                    onChange={(e) => setQuery(e.target.value)}
                                    aria-label={t('Search users')}
                                />
                            </div>
                        </div>
                    </div>

                    {filtered.length === 0 ? (
                        <div className="v3-sadmin-empty">
                            <UsersIcon size={28} aria-hidden />
                            <h3>{users.length === 0 ? t('No users yet') : t('No matches')}</h3>
                            <p>
                                {users.length === 0
                                    ? t('Add your first backend user with "New user".')
                                    : t('Try a different search.')}
                            </p>
                        </div>
                    ) : (
                        <div className="v3-sadmin-table-wrap">
                            <table className="v3-sadmin-table">
                                <thead>
                                    <tr>
                                        <th>#</th>
                                        <th>{t('Name')}</th>
                                        <th>{t('Email')}</th>
                                        <th>{t('Profile')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filtered.map((u, i) => (
                                        <tr key={u._id || i} onClick={() => handleUserClick(u._id)}>
                                            <td className="v3-sadmin-cell-muted">{i + 1}</td>
                                            <td className="v3-sadmin-cell-strong">
                                                {[u.userFirstName, u.userLastName].filter(Boolean).join(' ') || '—'}
                                            </td>
                                            <td className="v3-sadmin-cell-muted">{u.username || '—'}</td>
                                            <td>
                                                <span className="v3-sadmin-pill live">
                                                    {u.user_type_id?.user_role || t('Backend User')}
                                                </span>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </section>
            </div>
        );
    }

    // ── Add / Edit view ──────────────────────────────────────────────────
    const isEdit = viewType === 'Edit';
    const onSubmit = isEdit ? handleEditSubmit : handleAddSubmit;

    return (
        <div className="v3 v3-sadmin">
            <header className="v3-sadmin-hero">
                <div className="v3-sadmin-hero-text">
                    <div className="v3-label">{t('Settings · Users')}</div>
                    <h1 className="v3-display v3-sadmin-greeting" style={{ fontSize: 26 }}>
                        {isEdit ? t('Edit user') : t('New backend user')}
                    </h1>
                </div>
                <div className="v3-sadmin-hero-actions">
                    <button type="button" className="v3-btn v3-btn-ghost" onClick={clearAll}>
                        <ArrowLeft size={14} />
                        {t('Back to list')}
                    </button>
                </div>
            </header>

            <section className="v3-card v3-sadmin-form-card">
                <form className="v3-sadmin-form" onSubmit={onSubmit} autoComplete="off">
                    <div className="v3-sadmin-form-grid">
                        <div className="v3-field">
                            <label className="v3-label">{t('First name')}</label>
                            <input
                                className="v3-input"
                                type="text"
                                value={firstName}
                                onChange={(e) => setFirstName(e.target.value)}
                                required
                            />
                        </div>
                        <div className="v3-field">
                            <label className="v3-label">{t('Last name')}</label>
                            <input
                                className="v3-input"
                                type="text"
                                value={lastName}
                                onChange={(e) => setLastName(e.target.value)}
                                required
                            />
                        </div>
                        <div className="v3-field">
                            <label className="v3-label">{t('Email')}</label>
                            <input
                                className="v3-input"
                                type="text"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                            />
                        </div>
                        <div className="v3-field">
                            <label className="v3-label">{t('Profile')}</label>
                            <div className="v3-select-wrap">
                                <select
                                    className="v3-input v3-select"
                                    value={userProfileId || ''}
                                    onChange={() => {}}
                                    required
                                >
                                    <option value={userProfileId || ''}>{t('Backend User')}</option>
                                </select>
                                <ChevronDown size={14} className="v3-select-caret" aria-hidden />
                            </div>
                        </div>

                        <div className="v3-field" style={{ gridColumn: '1 / -1' }} ref={dropdownRef}>
                            <label className="v3-label">{t('Concessionaires')}</label>
                            <div className="v3-sadmin-multi">
                                <button
                                    type="button"
                                    className={`v3-sadmin-multi-toggle ${dropdownOpen ? 'open' : ''}`}
                                    onClick={() => setDropdownOpen((o) => !o)}
                                >
                                    <span className={selectedConcessionaires.length === 0 ? 'placeholder' : ''}>
                                        {selectedSummary}
                                    </span>
                                    <ChevronDown size={14} />
                                </button>
                                {dropdownOpen && (
                                    <div className="v3-sadmin-multi-menu">
                                        {concessionaires.length === 0 ? (
                                            <div className="v3-sadmin-multi-item">
                                                <span className="v3-sadmin-cell-muted">{t('No concessionaires loaded')}</span>
                                            </div>
                                        ) : (
                                            concessionaires.map((c) => (
                                                <label
                                                    key={c._id}
                                                    className="v3-sadmin-multi-item"
                                                    onClick={(e) => e.stopPropagation()}
                                                >
                                                    <input
                                                        type="checkbox"
                                                        checked={selectedConcessionaires.includes(c._id)}
                                                        onChange={() => toggleConcessionaire(c._id)}
                                                    />
                                                    {c.concessionaire_name}
                                                </label>
                                            ))
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="v3-sadmin-form-section">
                        <div className="v3-sadmin-form-section-title">{t('Permissions')}</div>
                        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                            <label className="v3-sadmin-checkbox-row">
                                <input
                                    type="checkbox"
                                    checked={deletePermission}
                                    onChange={(e) => setDeletePermission(e.target.checked)}
                                />
                                {t('Allow data deletion')}
                            </label>
                            {isEdit && (
                                <label className="v3-sadmin-checkbox-row">
                                    <input
                                        type="checkbox"
                                        checked={isDisable}
                                        onChange={(e) => setIsDisable(e.target.checked)}
                                    />
                                    {t('Disable account')}
                                </label>
                            )}
                        </div>
                    </div>

                    <div className="v3-sadmin-form-actions">
                        <button type="button" className="v3-btn v3-btn-ghost" onClick={clearAll}>
                            {t('Cancel')}
                        </button>
                        <button type="submit" className="v3-btn v3-btn-primary" disabled={submitting}>
                            <Save size={14} />
                            {submitting ? (isEdit ? t('Updating…') : t('Adding…')) : isEdit ? t('Update user') : t('Add user')}
                        </button>
                    </div>
                </form>
            </section>
        </div>
    );
};

export default ManageSadminUsers;
